import { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router';
import { Flex, Skeleton, Stack, Table } from '@chakra-ui/react';
import { ColumnDef, flexRender, Row } from '@tanstack/react-table';

import { useTable } from '~/core/shared/components/Table';
import { TableSortIcon } from '~/core/shared/components/Table/TableSortIcon';
import { EMPTY_ARR } from '~/core/shared/constants';
import {
  ApiPaginationMetaResponse,
  Pagination,
  Sorting,
} from '~/core/shared/types';
import { Pagination as PaginationComponent } from '../Pagination.component';

type ReactQueryTableProps<D extends Record<string, unknown>> = {
  columns: ColumnDef<D>[];
  data?: D[];
  meta?: ApiPaginationMetaResponse;
  isLoading?: boolean;
  sorting: Sorting;
  pagination: Pagination;
  onSortingChange: (sorting: Sorting) => void;
  onPaginationChange: (pagination: Pagination) => void;
  rowPath?: (data: D) => string;
  getRowProps?: (row: Row<D>) => Record<string, unknown> | undefined;
};

export function ReactQueryTable<D extends Record<string, unknown>>({
  columns,
  data = EMPTY_ARR,
  meta,
  isLoading = false,
  sorting,
  pagination,
  onSortingChange,
  onPaginationChange,
  rowPath,
  getRowProps,
}: ReactQueryTableProps<D>): ReactElement {
  const navigate = useNavigate();

  const { getHeaderGroups, getRowModel, numberOfCols, setPage } = useTable({
    columns,
    data,
    meta,
    sorting,
    onSortingChange,
    pagination,
    onPaginationChange,
  });

  return (
    <Stack direction="column" gap={4}>
      <Table.Root>
        <Table.Header>
          {getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.ColumnHeader
                  key={header.id}
                  cursor={header.column.getCanSort() ? 'pointer' : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Flex alignItems="center" gap={2}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <TableSortIcon
                        direction={header.column.getIsSorted()}
                        isActive={!!header.column.getIsSorted()}
                      />
                    )}
                  </Flex>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          ))}
        </Table.Header>

        <Table.Body>
          {isLoading && (
            <Table.Row>
              <Table.Cell colSpan={numberOfCols}>
                <Stack gap={4}>
                  <Skeleton height={8} />
                  <Skeleton height={8} />
                  <Skeleton height={8} />
                </Stack>
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading &&
            meta?.total !== 0 &&
            getRowModel().rows.map((row) => (
              <Table.Row
                onClick={(e) =>
                  rowPath && e.button === 0 && navigate(rowPath(row.original))
                }
                {...(getRowProps && getRowProps(row))}
                key={row.id}
                _hover={rowPath && { bg: '#F9FAFC' }}
                cursor={rowPath && 'pointer'}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>
                    {rowPath ? (
                      <Link to={rowPath(row.original)}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Link>
                    ) : (
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </>
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          {!isLoading && meta?.total === 0 && (
            <Table.Row>
              <Table.Cell colSpan={numberOfCols}>
                <Flex justifyContent="center">No records</Flex>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
      {meta && (
        <PaginationComponent
          canNextPage={meta.currentPage < meta.lastPage}
          canPreviousPage={meta.currentPage > 1}
          gotoPage={(idx) => setPage(idx + 1)}
          meta={meta}
          nextPage={() => setPage(meta.currentPage + 1)}
          previousPage={() => setPage(meta.currentPage - 1)}
        />
      )}
    </Stack>
  );
}
